// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import { Button, Text, useMantineTheme } from '@mantine/core';
import { useForm } from '@mantine/form';
// oxlint-disable-next-line no-restricted-imports
import { Link, useNavigate } from 'react-router';

import { NewPasswordInput } from 'app/Layout/HeaderBar/ProfileDropdown/ResetPasswordModal/NewPasswordInput';

import { type InvitationType, useConfirmUser } from '../auth/restCalls';
import { AuthFormWrapper } from '../components/AuthFormWrapper';
import type { LoginErrorCode } from '../components/LoginErrorAlert';

type Props = {
  inviteSourceName?: string | null;
  type: InvitationType;
  id: string;
  code: string;
  idp: string | null;
};

export const Join = ({ inviteSourceName, type, id, code, idp }: Props) => {
  const theme = useMantineTheme();
  const { confirmUser, confirming } = useConfirmUser(true);
  const [passwordIsValid, setPasswordIsValid] = useState(false);
  // useEvolveNavigate only works inside Shop routes
  const navigate = useNavigate();
  const [errorCode, setErrorCode] = useState<LoginErrorCode>();

  const form = useForm<{
    password: string;
    confirmationCode: string;
  }>({
    initialValues: {
      password: '',
      confirmationCode: code,
    },
  });

  const handleSubmit = (body: typeof form.values) => {
    confirmUser(id, type, body)
      .then(() => navigate('/auth/login', { state: { inviteConfirm: true } }))
      // oxlint-disable-next-line typescript/no-explicit-any
      .catch(({ message, code: errCode }: any) => setErrorCode(message ?? errCode));
  };

  return (
    <AuthFormWrapper
      title={`You have been invited to join ${inviteSourceName}`}
      errorCode={errorCode}
      onCloseError={() => setErrorCode(undefined)}
      form={form}
      onSubmit={handleSubmit}
    >
      {!idp && (
        <>
          <NewPasswordInput
            disabled={confirming}
            setPasswordIsValid={setPasswordIsValid}
            {...form.getInputProps('password')}
          />
          <Button
            type="submit"
            fullWidth
            loading={confirming}
            disabled={!passwordIsValid}
            color={`${theme.primaryColor}.9`}
          >
            Join {type === 'department' ? 'facility' : type}
          </Button>
          <Text c="dimmed" ta="center" fz="sm">
            Already have an account?{' '}
            <Link to="/auth/login" className="link">
              <Text c={`${theme.primaryColor}.9`} span>
                Login here.
              </Text>
            </Link>
          </Text>
        </>
      )}
    </AuthFormWrapper>
  );
};
